import type { EventEntityDefinition } from '@gzl10/nexus-sdk'
import { useIdField, useTextField, useNumberField, useSelectField, useDatetimeField, useTextareaField } from '@gzl10/nexus-sdk/fields'

/**
 * Migration History Entity
 *
 * Provides read-only access to migration execution history.
 * Maps to _nexus_migrations table created by the migration system.
 *
 * Only accessible by ADMIN role.
 */
export const migrationHistoryEntity: EventEntityDefinition = {
  type: 'event',
  immutable: true,
  label: { en: 'Migration History', es: 'Historial de Migraciones' },
  icon: 'mdi:database-sync',
  labelField: 'name',
  routePrefix: '/migration-history',
  table: '_nexus_migrations',
  timestamps: true,
  calendarFrom: 'executed_at',

  fields: {
    id: useIdField(),
    name: useTextField({ label: { en: 'Migration Name', es: 'Nombre de la Migración' }, required: true, size: 255, unique: true, meta: { sortable: true, searchable: true } }),
    batch: useNumberField({ label: { en: 'Batch', es: 'Lote' }, required: true, meta: { sortable: true } }),
    status: useSelectField({
      label: { en: 'Status', es: 'Estado' },
      options: [
        { value: 'running', label: { en: 'Running', es: 'Ejecutando' } },
        { value: 'completed', label: { en: 'Completed', es: 'Completada' } },
        { value: 'failed', label: { en: 'Failed', es: 'Fallida' } },
        { value: 'rolled_back', label: { en: 'Rolled Back', es: 'Revertida' } }
      ],
      required: true,
      size: 20,
      meta: { sortable: true }
    }),
    executed_at: useDatetimeField({ label: { en: 'Executed At', es: 'Ejecutada el' }, meta: { sortable: true } }),
    rolled_back_at: useDatetimeField({ label: { en: 'Rolled Back At', es: 'Revertida el' }, meta: { sortable: true } }),
    execution_time_ms: useNumberField({ label: { en: 'Duration (ms)', es: 'Duración (ms)' }, meta: { sortable: true } }),
    error: useTextareaField({ label: { en: 'Error Message', es: 'Mensaje de Error' } })
  },

  casl: {
    subject: 'SystemMigrationHistory',
    permissions: {
      ADMIN: { actions: ['read'] },
      SUPPORT: { actions: ['read'] }
    }
  },

  // Keep migration history for 1 year
  retention: { days: 365 },

  defaultSort: { field: 'executed_at', order: 'desc' }
}
